import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import PreviewIcon from '@mui/icons-material/Preview';
import SendIcon from '@mui/icons-material/Send';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from './api';
import qs from 'qs';
import { useUser } from './UserContext';

const EmailContestantsPage = () => {
  const { user } = useUser();
  const { clubShortName, contestShortName } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // State for loading
  const [loading, setLoading] = useState(true);
  const [club, setClub] = useState(null);
  const [contest, setContest] = useState(null);
  const [error, setError] = useState('');

  // State for member selection
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState(new Set());

  const recaptchaRef = useRef(null);

  // State for email composition
  const [emailDescription, setEmailDescription] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState(false);

  // Load club and contest data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load club data
        const clubResponse = await api.get(`/club/${clubShortName}`, {
          headers: {
            'Authentication': user.authentication
          }
        });
        const clubData = clubResponse.data.result;
        setClub(clubData);

        // Load contest data
        const contestResponse = await api.get(`/club/${clubShortName}/contest/${contestShortName}`, {
          headers: {
            'Authentication': user.authentication
          }
        });
        const contestData = contestResponse.data.result;
        setContest(contestData);

        // Check permissions - only owners and committee members can access this page
        const canEmail = clubData.userRole === 'owner' ||
                         (contestData.contestCommittee && contestData.contestCommittee.includes(user.userEmail));

        if (!canEmail) {
          navigate(`/club/${clubShortName}/contest/${contestShortName}`);
          return;
        }

        // Set up members list
        const membersList = clubData.clubMembers || [];
        setMembers(membersList);

        // Initially select all members - using userGuid instead of email
        const allMemberGuids = new Set(membersList.map(member => member.userGuid));
        setSelectedMembers(allMemberGuids);

      } catch (err) {
        setError('Failed to load contest and club data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.authentication && clubShortName && contestShortName) {
      fetchData();
    }
  }, [user, clubShortName, contestShortName, navigate]);

  const handleSelectAll = () => {
    const allMemberGuids = new Set(members.map(member => member.userGuid));
    setSelectedMembers(allMemberGuids);
  };

  const handleDeselectAll = () => {
    setSelectedMembers(new Set());
  };

  const handleMemberToggle = (userGuid) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(userGuid)) {
      newSelected.delete(userGuid);
    } else {
      newSelected.add(userGuid);
    }
    setSelectedMembers(newSelected);
  };

  const generateEmailPreview = () => {
    if (!emailDescription.trim()) {
      return '';
    }

    // Use Material-UI theme to detect dark mode
    const isDarkMode = theme.palette.mode === 'dark';
    const bodyBgColor = isDarkMode ? '#3c3c3c' : '#f4f4f4';
    const emailBgColor = isDarkMode ? '#333333' : 'white';
    const textColor = isDarkMode ? 'white' : '#333';
    const footerTextColor = isDarkMode ? 'white' : '#888';

    const messageHtml = emailDescription.split('\n').map(line =>
      line.trim() ? `<p style="color: ${textColor}; margin: 0.5em 0;">${line}</p>` : '<br>'
    ).join('');

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${contest?.contestName} - Message from ${club?.clubName}</title>
</head>
<body style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; background-color: ${bodyBgColor}; padding: 20px; color: ${textColor};">
  <table align="center" width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; background-color: ${emailBgColor}; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.05);">
    <tr>
      <td style="background-color: #eadfcf; padding: 30px;">
        <table width="100%">
          <tr>
            <td align="center">
              <img src="/mascot.png" alt="Snapperish Logo" style="height: 80px;" />
            </td>
          </tr>
          <tr>
            <td align="center">
              <h1 style="color: white !important; margin: 10px 0 0;">${contest?.contestName}</h1>
            </td>
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding: 30px;">
        <p style="font-size: 18px; color: ${textColor}; margin: 0 0 1em 0;">Hello from ${club?.clubName}!</p>
        ${messageHtml}
        <div style="text-align: center; margin: 30px 0;">
          <a href="${process.env.REACT_APP_WEB_URL}/club/${clubShortName}/contest/${contestShortName}" style="background-color: #311503; color: #fff; text-decoration: none; padding: 12px 24px; border-radius: 5px; font-weight: bold; display: inline-block;">View Contest</a>
        </div>
        <p style="margin-top: 40px; font-size: 14px; color: ${footerTextColor} !important;">This message was sent by a ${club?.clubName} contest organizer through Snapperish.</p>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const handleSendClick = () => {
    if (selectedMembers.size === 0) {
      setSendError('Please select at least one member to email');
      return;
    }
    if (!emailDescription.trim()) {
      setSendError('Please enter a message');
      return;
    }
    setSendError('');
    try {
      if (recaptchaRef.current) {
        recaptchaRef.current.execute().catch(() => {});
      } else {
        setSendError('Security verification not ready. Please try again.');
      }
    } catch (err) {
      setSendError('Security verification failed. Please refresh and try again.');
    }
  };

  const handleSendEmails = async (captchaToken) => {
    setIsSending(true);
    setSendError('');

    try {
      const selectedUserGuids = Array.from(selectedMembers);

      const data = {
        userGuidList: JSON.stringify(selectedUserGuids),
        emailDescription: emailDescription.trim(),
        captchaToken
      };

      const response = await api.post(`/club/${clubShortName}/contest/${contestShortName}/emailcontestants`, qs.stringify(data), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authentication': user.authentication
        }
      });

      if (response.status === 200) {
        setSendSuccess(true);
        setSendError('');
      } else {
        setSendError(response.statusText || 'Failed to send emails');
      }
    } catch (error) {
      setSendError(error.response?.data || 'Network error. Please try again.');
      if (recaptchaRef.current) {
        try { recaptchaRef.current.reset(); } catch {}
      }
    } finally {
      setIsSending(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading contest data...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!club || !contest) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Contest or club not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Email Contestants
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {contest.contestName} - {club.clubName}
            </Typography>
          </Box>
        </Box>

        {sendSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Emails sent successfully to {selectedMembers.size} member{selectedMembers.size === 1 ? '' : 's'}!
          </Alert>
        )}

        {/* Member Selection */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Select Recipients ({selectedMembers.size} of {members.length} selected)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<SelectAllIcon />}
                onClick={handleSelectAll}
                disabled={selectedMembers.size === members.length}
              >
                Select All
              </Button>
              <Button
                size="small"
                startIcon={<DeselectIcon />}
                onClick={handleDeselectAll}
                disabled={selectedMembers.size === 0}
              >
                Deselect All
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedMembers.size > 0 && selectedMembers.size < members.length}
                      checked={selectedMembers.size === members.length && members.length > 0}
                      onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  {(club?.userRole === 'owner' || !club?.clubHideEmails) && <TableCell>Email</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member, index) => (
                  <TableRow key={index} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedMembers.has(member.userGuid)}
                        onChange={() => handleMemberToggle(member.userGuid)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {member.userFullName}
                      </Typography>
                    </TableCell>
                    {(club?.userRole === 'owner' || !club?.clubHideEmails) && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {member.userEmail}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Message Composition */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6">
              Compose Message
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <InfoOutlinedIcon fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Limited to one bulk email per contest per day
              </Typography>
            </Box>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Message"
            value={emailDescription}
            onChange={(e) => setEmailDescription(e.target.value)}
            placeholder="Enter your message to contest participants..."
            helperText="This message will be sent to all selected members with a link to view the contest."
          />
        </Box>

        {/* Preview Toggle */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<PreviewIcon />}
            onClick={() => setShowPreview(!showPreview)}
            disabled={!emailDescription.trim()}
          >
            {showPreview ? 'Hide Preview' : 'Show Email Preview'}
          </Button>
        </Box>

        {/* Email Preview */}
        {showPreview && emailDescription.trim() && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Email Preview
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: 'background.paper',
                    maxHeight: 400,
                    overflow: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start'
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: generateEmailPreview()
                    }}
                    style={{
                      transform: 'scale(0.8)',
                      transformOrigin: 'top center'
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Error Display */}
        {sendError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {sendError}
          </Alert>
        )}

        {/* Send Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={isSending ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSendClick}
            disabled={isSending || selectedMembers.size === 0 || !emailDescription.trim()}
          >
            {isSending ? 'Sending...' : `Send Email to ${selectedMembers.size} Member${selectedMembers.size === 1 ? '' : 's'}`}
          </Button>
        </Box>

        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey="6Leh4YsrAAAAADnPrhjhWQ9E1wL1vAloifB3QOtr"
          size="invisible"
          onChange={(token) => {
            if (token) handleSendEmails(token);
          }}
        />
      </Paper>
    </Container>
  );
};

export default EmailContestantsPage;